import { Activity, CheckCircle2, Clock, PauseCircle, Ban, Rocket, CreditCard, Sparkles } from "lucide-react";

/**
 * CampaignStatusPill — derives a rich, human status from the raw
 * `status` / `external_provider_id` / `current_completes` / `target_completes`
 * fields and renders a coloured chip with a pulse dot for transient states.
 *
 * The status timeline (happy path):
 *   Draft → Awaiting Payment → Payment Confirmed → Live on Panel → Filled
 *                             (pulsing while PS activates the survey)
 */

// Status keys — kept as an exported enum so the parent dashboard can
// decide when to POLL (any campaign in a transient state triggers a
// 4-second poll on /api/campaigns; polling stops when everything is
// resolved).
export const TRANSIENT_STATUSES = new Set(["pending_payment", "activating"]);

export function deriveStatus(c) {
  const status = c?.status || "draft";
  const providerId = c?.external_provider_id || null;
  const target = c?.target_completes || 0;
  const current = c?.current_completes || 0;
  if (status === "draft") return "draft";
  if (status === "pending_payment") return "pending_payment";
  if (status === "active" && !providerId) return "activating";
  if (status === "active" && target > 0 && current >= target) return "filled";
  if (status === "active") return "live";
  if (status === "paused") return "paused";
  if (status === "stopping" || status === "stopped") return "stopped";
  if (status === "filled") return "filled";
  return status;
}

const PILL_META = {
  draft: {
    label: "Draft",
    icon: Clock,
    tone: "grey",
    pulse: false,
    hint: "Not yet submitted",
  },
  pending_payment: {
    label: "Payment Pending",
    icon: CreditCard,
    tone: "amber",
    pulse: true,
    hint: "Awaiting Stripe confirmation",
  },
  activating: {
    label: "Activating Survey",
    icon: Rocket,
    tone: "blue",
    pulse: true,
    hint: "Payment confirmed · launching on panel",
  },
  live: {
    label: "Live · Fielding",
    icon: Activity,
    tone: "green",
    pulse: true,
    hint: "Collecting responses",
  },
  filled: {
    label: "Filled",
    icon: CheckCircle2,
    tone: "brand",
    pulse: false,
    hint: "Target completes reached",
  },
  paused: {
    label: "Paused",
    icon: PauseCircle,
    tone: "grey",
    pulse: false,
    hint: "No new responses",
  },
  stopped: {
    label: "Stopped",
    icon: Ban,
    tone: "red",
    pulse: false,
    hint: "Campaign closed early",
  },
};

// Colour tokens — tuned to the Adrigo palette (see design_guidelines.md).
// `bg` is deliberately low-opacity so the pill sits ambient on the white
// card background; `text` and `dot` carry the semantic weight.
const TONES = {
  grey:  { text: "text-[var(--rl-text-secondary)]", bg: "bg-[var(--rl-bg-subtle)]",   dot: "bg-[var(--rl-text-secondary)]" },
  amber: { text: "text-amber-700",                  bg: "bg-amber-50",                dot: "bg-amber-500" },
  blue:  { text: "text-sky-700",                    bg: "bg-sky-50",                  dot: "bg-sky-500" },
  green: { text: "text-emerald-700",                bg: "bg-emerald-50",              dot: "bg-emerald-500" },
  brand: { text: "text-[var(--rl-brand)]",          bg: "bg-[color-mix(in_srgb,var(--rl-brand)_10%,white)]", dot: "bg-[var(--rl-brand)]" },
  red:   { text: "text-red-700",                    bg: "bg-red-50",                  dot: "bg-red-500" },
};

export function CampaignStatusPill({ campaign, size = "md" }) {
  const key = deriveStatus(campaign);
  const meta = PILL_META[key] || PILL_META.draft;
  const tone = TONES[meta.tone] || TONES.grey;
  const Icon = meta.icon;
  const px = size === "sm" ? "px-2 py-0.5" : "px-2.5 py-1";
  const txt = size === "sm" ? "text-[10px]" : "text-[11px]";
  return (
    <div
      className={`inline-flex items-center gap-1.5 ${px} ${tone.bg} ${tone.text} uppercase tracking-[0.15em] font-bold ${txt}`}
      title={meta.hint}
      data-testid={`campaign-status-pill-${key}`}
      data-status={key}
    >
      {meta.pulse ? <PulseDot toneClass={tone.dot} /> : <Icon className="w-3 h-3" />}
      <span>{meta.label}</span>
      {key === "activating" && <Sparkles className="w-3 h-3 opacity-70" />}
    </div>
  );
}

function PulseDot({ toneClass }) {
  // Two-layer dot: solid dot on top + expanding-ring on `animate-ping`
  // below. Emits a soft heartbeat that reads clearly at 12px without
  // being distracting on a grid of cards.
  return (
    <span className="relative inline-flex w-2 h-2">
      <span className={`absolute inline-flex w-full h-full rounded-full ${toneClass} opacity-70 animate-ping`} />
      <span className={`relative inline-flex w-2 h-2 rounded-full ${toneClass}`} />
    </span>
  );
}
